const DEFAULT_POST_LOGIN_DESTINATION = '/mobile';
const NUMBERED_ACCOUNT_DESTINATION = '/inbox';
const DEFAULT_LANDING_PATHS = new Set([
  '/',
  '/dashboard',
  '/dashboard-selector',
  '/inbox',
  '/mobile',
]);

type FetchLike = typeof fetch;

interface PostLoginDestinationParams {
  requestedPath?: string | null;
  hasPhoneNumber: boolean;
  fallbackPath?: string;
}

interface ResolvePostLoginDestinationParams {
  requestedPath?: string | null;
  token?: string | null;
  fetchImpl?: FetchLike;
}

function getPathname(path: string): string {
  return path.split(/[?#]/, 1)[0] || '/';
}

function normalizeRequestedPath(path?: string | null): string | null {
  if (!path) return null;
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  if (path.startsWith('/auth')) return null;
  return path;
}

function isDefaultLanding(path: string | null): boolean {
  return !path || DEFAULT_LANDING_PATHS.has(getPathname(path));
}

export function hasPhoneNumberFromResponse(payload: unknown): boolean {
  if (Array.isArray(payload)) {
    return payload.length > 0;
  }

  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const response = payload as { phoneNumbers?: unknown; numbers?: unknown };
  if (Array.isArray(response.phoneNumbers)) {
    return response.phoneNumbers.length > 0;
  }

  if (Array.isArray(response.numbers)) {
    return response.numbers.length > 0;
  }

  return false;
}

export function getPostLoginDestination({
  requestedPath,
  hasPhoneNumber,
  fallbackPath = DEFAULT_POST_LOGIN_DESTINATION,
}: PostLoginDestinationParams): string {
  const safeRequestedPath = normalizeRequestedPath(requestedPath);

  // The null check is redundant at runtime — isDefaultLanding(null) is true, so
  // this branch never ran for null — but it lets the compiler narrow, which it
  // cannot do through the helper. Two of the repo's tsc errors lived here.
  if (safeRequestedPath && !isDefaultLanding(safeRequestedPath)) {
    return safeRequestedPath;
  }

  return hasPhoneNumber ? NUMBERED_ACCOUNT_DESTINATION : fallbackPath;
}

/**
 * Does this account have a number? `null` means WE COULD NOT TELL.
 *
 * ⚠ This used to collapse every outcome into `false` — a non-ok response, a
 * dropped request, an /api path that answered with the SPA shell so `.json()`
 * threw. "No number" and "could not ask" are different facts, and merging them
 * sent established customers who own a number to the new-user landing
 * (`/mobile`) whenever a single request happened to fail at login time. On a
 * flaky connection that is precisely when it fails.
 */
async function fetchHasPhoneNumber(
  token: string | null | undefined,
  fetchImpl: FetchLike,
): Promise<boolean | null> {
  if (!token) return null;

  try {
    const response = await fetchImpl('/api/voice/available-numbers', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    return hasPhoneNumberFromResponse(await response.json());
  } catch {
    return null;
  }
}

export async function resolvePostLoginDestination({
  requestedPath,
  token,
  fetchImpl = fetch,
}: ResolvePostLoginDestinationParams): Promise<string> {
  const safeRequestedPath = normalizeRequestedPath(requestedPath);

  // The null check is redundant at runtime — isDefaultLanding(null) is true, so
  // this branch never ran for null — but it lets the compiler narrow, which it
  // cannot do through the helper. Two of the repo's tsc errors lived here.
  if (safeRequestedPath && !isDefaultLanding(safeRequestedPath)) {
    return safeRequestedPath;
  }

  const hasPhoneNumber = await fetchHasPhoneNumber(token, fetchImpl);

  // Could not tell → send them to the app, not to the new-user landing.
  // Someone who just typed a correct password has an account; guessing "no
  // number" on a failed lookup is the guess that strands existing customers.
  // `/inbox` degrades gracefully for an account that genuinely has no number;
  // `/mobile` does not degrade gracefully for one that does.
  if (hasPhoneNumber === null) return NUMBERED_ACCOUNT_DESTINATION;

  return getPostLoginDestination({ requestedPath: safeRequestedPath, hasPhoneNumber });
}
